import { memo } from "react";
import { generatePath, Link, useParams } from "react-router";

import type { InputMaybe } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types";

import { Button } from "@/components/ui/button/button";
import { InputNumberField } from "@/components/ui/form/fields/input-field/input-number";

import { QuestionSectionWrapper } from "../../../../../_shared/ui/components/system/question/_layouts/item-wrapper";

type Props = {
	isEdited: boolean;
	hasError: boolean;

	maxDailyStockNum: ForNodeType<InputMaybe<number> | undefined> | undefined;
	onSetMaxDailyStockNum: (value: number | undefined) => void;

	title?: string;
	guide?: string;
};

export const QuestionOptionMaxDailyStockNum = memo(
	({ isEdited, hasError, maxDailyStockNum, onSetMaxDailyStockNum, title: _title, guide: _guide }: Props) => {
		const { hotelID } = useParams<{ hotelID: string }>();
		const optionStockPath = generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK, { hotelID: hotelID! });

		const title = _title ?? "日ごとの在庫数";
		const guide =
			_guide ??
			"1日ごとのオプションの提供可能数を設定できます。<br/>未入力の場合は上限なしで予約可能になります。<br />サブオプションの場合は、サブオプションの1日あたりの在庫の合計が日ごとの在庫数に設定されます。";

		return (
			<FeatureIslandCategoryQuestionForMain
				required={false}
				title={title}
				guide={guide}
				isEdited={isEdited}
				hasError={hasError}
				inputChildren={
					<>
						<div>
							<p className="text-sm text-error-600">
								※日ごとの在庫を
								<Button variant="link-blue" asChild className="p-0">
									<Link
										to={optionStockPath}
										className="mx-1 font-bold underline-offset-4 hover:underline"
										viewTransition
										target="_blank"
									>
										在庫管理画面
									</Link>
								</Button>
								で設定している場合はそちらが優先されますのでご注意ください。
							</p>
						</div>
						<QuestionSectionWrapper>
							<InputNumberField
								value={maxDailyStockNum?.value ?? undefined}
								handleInput={onSetMaxDailyStockNum}
								errorMessage={maxDailyStockNum?.error}
								hiddenSpinButton={false}
							/>
							<p>個まで</p>
						</QuestionSectionWrapper>
					</>
				}
			/>
		);
	},
);

QuestionOptionMaxDailyStockNum.displayName = "QuestionOptionMaxDailyStockNum";
